"use client";

import { useState } from "react";
import {
  Notice,
  type Option,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  CapmBlock,
  RegressionBlock,
} from "@/components/tools/accounting/capm-results";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type BetaRegression,
  parsePairs,
  regressBeta,
} from "@/lib/tool-kit/accounting/capm";

/** English strings. A translated content file overrides these through `ui`. */
export const CAPM_CALCULATOR_STRINGS = {
  inputsHeading: "Your inputs",
  modeLabel: "Beta",
  modeEnter: "I know the beta",
  modeData: "Estimate beta from returns",
  marketLabel: "Market input",
  marketReturn: "Expected market return",
  marketPremiumOption: "Market risk premium",
  riskFree: "Risk-free rate (% a year)",
  marketReturnInput: "Expected market return (% a year)",
  premiumInput: "Market risk premium (% a year)",
  betaInput: "Beta",
  pairsLabel: "Returns — one period per line: asset, market (in %)",
  pairsHint:
    "Separate the two numbers with a space, tab or semicolon. Use the same period length for both (daily, weekly or monthly). Returns are percentages, so 2.1 means 2.1%.",
  examplePairs:
    "2.9 1.5\n-1.9 -0.8\n3.1 2.6\n2.4 0.7\n-4.6 -2.4\n5.2 3.1\n0.4 1.0\n0.8 -0.1\n3.6 2.0\n-1.5 -1.7\n2.8 2.2\n0.2 0.9",
  periodRf: "Risk-free return per period (%)",
  periodRfHint:
    "Subtracted from both series, so the regression uses excess returns and alpha is Jensen's alpha. Leave 0 for a beta on raw returns; the beta is almost unchanged either way.",
  convention:
    "CAPM: expected return = risk-free rate + beta × (market return − risk-free rate). Rates are annual and in percent; beta has no unit. Entering the market risk premium instead of the market return gives the same result.",
  resultsHeading: "Expected return (CAPM)",
  expected: "Expected return",
  marketPremium: "Market risk premium",
  assetPremium: "Asset risk premium (β × premium)",
  betaUsed: "Beta used",
  workingHeading: "The working",
  workReturn:
    "Expected return = Rf + β × (Rm − Rf) = {rf} + {beta} × ({rm} − {rf}) = {result}",
  workPremium:
    "Expected return = Rf + β × market risk premium = {rf} + {beta} × {premium} = {result}",
  smlTitle: "Security market line",
  smlX: "Beta",
  smlY: "Expected return (%)",
  smlDescription:
    "Line chart of the security market line: expected return rises in a straight line from {rf}% at beta 0 to {rm}% at beta 1, with your asset marked at beta {beta}.",
  seriesLine: "Security market line",
  seriesAsset: "Your asset",
  tableHeading: "Required return at other betas",
  betaColumn: "Beta",
  requiredColumn: "Expected return",
  betaHeading: "Beta from your returns",
  beta: "Beta (slope)",
  alpha: "Alpha per period",
  rSquared: "R²",
  correlation: "Correlation",
  standardError: "Standard error of beta",
  interval: "95% interval for beta",
  adjusted: "Adjusted beta (⅔β + ⅓)",
  observations: "Periods",
  betaWorking:
    "Beta = Cov(asset, market) ÷ Var(market) = {cov} ÷ {variance} = {beta}",
  fewObservations:
    "Fewer than 24 periods: a beta from so few observations has a wide margin of error. Five years of monthly returns is a common choice.",
  scatterTitle: "Asset returns against market returns",
  scatterX: "Market return (%)",
  scatterY: "Asset return (%)",
  scatterDescription:
    "Scatter plot of {n} paired returns with the least-squares line; its slope is the beta, {beta}.",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy data",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  needRates:
    "Enter the risk-free rate and the market figure as percentages, and a beta.",
  invalidLine:
    "“{text}” is not a pair of numbers. Put the asset return and the market return on each line.",
  tooFew: "Enter at least two periods of returns.",
  flatMarket:
    "The market return is the same in every period, so beta cannot be estimated.",
  lengthMismatch: "Each line needs an asset return and a market return.",
} as const;

export type CapmCalculatorStrings = typeof CAPM_CALCULATOR_STRINGS;

type Mode = "enter" | "data";
type MarketKind = "return" | "premium";

interface Form {
  mode: Mode;
  kind: MarketKind;
  rf: string;
  rm: string;
  premium: string;
  beta: string;
  pairs: string;
  rfPeriod: string;
}

type Evaluated =
  | {
      problem: null;
      beta: number;
      rf: number;
      rm: number;
      pairs: ReturnType<typeof parsePairs>;
      periodRate: number;
      regression: BetaRegression | null;
    }
  | { problem: string };

function evaluate(form: Form, strings: CapmCalculatorStrings): Evaluated {
  const rfRate = parseNumberInput(form.rf);
  const figure = parseNumberInput(
    form.kind === "return" ? form.rm : form.premium
  );
  if (rfRate === null || figure === null) {
    return { problem: strings.needRates };
  }
  const rm = (form.kind === "return" ? figure : rfRate + figure) / 100;
  const pairs = parsePairs(form.pairs, parseNumberInput);
  const periodRate = parseNumberInput(form.rfPeriod) ?? 0;
  const base = { problem: null, rf: rfRate / 100, rm, pairs, periodRate };
  if (form.mode === "enter") {
    const beta = parseNumberInput(form.beta);
    return beta === null
      ? { problem: strings.needRates }
      : { ...base, beta, regression: null };
  }
  if (pairs.invalid !== null) {
    return { problem: t(strings.invalidLine, { text: pairs.invalid }) };
  }
  const regression = regressBeta(pairs.asset, pairs.market, periodRate);
  if ("error" in regression) {
    return { problem: strings[regression.error] };
  }
  return { ...base, beta: regression.beta, regression };
}

export default function CapmCalculatorWidget() {
  const strings = useToolUi(CAPM_CALCULATOR_STRINGS);
  const [form, setForm] = useState<Form>({
    mode: "enter",
    kind: "return",
    rf: "3",
    rm: "8",
    premium: "5",
    beta: "1.2",
    pairs: CAPM_CALCULATOR_STRINGS.examplePairs,
    rfPeriod: "0",
  });
  const update = (patch: Partial<Form>) =>
    setForm((old) => ({ ...old, ...patch }));
  const modes: Option<Mode>[] = [
    { value: "enter", label: strings.modeEnter },
    { value: "data", label: strings.modeData },
  ];
  const kinds: Option<MarketKind>[] = [
    { value: "return", label: strings.marketReturn },
    { value: "premium", label: strings.marketPremiumOption },
  ];
  const result = evaluate(form, strings);

  return (
    <div className="space-y-6">
      <section className={`${CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.inputsHeading}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            id="capm-mode"
            label={strings.modeLabel}
            onChange={(mode) => update({ mode })}
            options={modes}
            value={form.mode}
          />
          <SelectField
            id="capm-kind"
            label={strings.marketLabel}
            onChange={(kind) => update({ kind })}
            options={kinds}
            value={form.kind}
          />
          <TextField
            id="capm-rf"
            label={strings.riskFree}
            numeric
            onChange={(rf) => update({ rf })}
            value={form.rf}
          />
          {form.kind === "return" ? (
            <TextField
              id="capm-rm"
              label={strings.marketReturnInput}
              numeric
              onChange={(rm) => update({ rm })}
              value={form.rm}
            />
          ) : (
            <TextField
              id="capm-premium"
              label={strings.premiumInput}
              numeric
              onChange={(premium) => update({ premium })}
              value={form.premium}
            />
          )}
          {form.mode === "enter" ? (
            <TextField
              id="capm-beta"
              label={strings.betaInput}
              numeric
              onChange={(beta) => update({ beta })}
              value={form.beta}
            />
          ) : (
            <TextField
              hint={strings.periodRfHint}
              id="capm-period-rf"
              label={strings.periodRf}
              numeric
              onChange={(rfPeriod) => update({ rfPeriod })}
              value={form.rfPeriod}
            />
          )}
        </div>
        {form.mode === "data" ? (
          <TextAreaField
            hint={strings.pairsHint}
            id="capm-pairs"
            label={strings.pairsLabel}
            onChange={(pairs) => update({ pairs })}
            rows={8}
            value={form.pairs}
          />
        ) : null}
        <p className={HINT}>{strings.convention}</p>
      </section>
      {result.problem === null ? (
        <>
          {result.regression ? (
            <RegressionBlock
              asset={result.pairs.asset}
              fit={result.regression}
              market={result.pairs.market}
              rfPeriod={result.periodRate}
              strings={strings}
            />
          ) : null}
          <CapmBlock
            beta={result.beta}
            premiumGiven={form.kind === "premium"}
            rf={result.rf}
            rm={result.rm}
            strings={strings}
          />
        </>
      ) : (
        <Notice>{result.problem}</Notice>
      )}
    </div>
  );
}
